import React from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { ArrowRight, BookOpen, Compass, Target, Sparkles, ShieldCheck, Users } from "lucide-react";

/**
 * "About / Founder" page — humanises ANATRADEX.
 * Author identity kept to initials F.L. per user preference.
 */
export default function About() {
  usePageMeta({
    title: "À propos · ANATRADEX — L'histoire derrière la plateforme",
    description: "ANATRADEX est né de la frustration d'un trader autodidacte face à l'opacité des outils de trading algorithmiques. Voici son histoire.",
    og: {
      title: "À propos · ANATRADEX",
      description: "L'histoire d'un trader autodidacte qui a créé les outils qu'il aurait voulu avoir dès le début.",
    },
  });

  return (
    <div className="min-h-screen bg-bg text-white/85" data-testid="about-page">
      {/* NAV */}
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-accent-blue to-blue-600 flex items-center justify-center font-bold text-lg">P</div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Outil d'Analyse Statistique</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing" className="text-sm text-white/60 hover:text-white">Tarifs</Link>
            <Link to="/academy" className="text-sm text-white/60 hover:text-white">Academy</Link>
            <Link to="/app" className="btn">Dashboard</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-accent-blue/80 mb-3">L'histoire de ANATRADEX</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6" data-testid="about-title">
          Un trader autodidacte,<br />
          <span className="text-accent-blue">frustré par l'opacité</span> des outils qu'on lui vendait.
        </h1>
        <p className="text-lg text-white/60 max-w-3xl leading-relaxed">
          ANATRADEX n'a pas été conçu par une équipe de docteurs en finance quantitative dans un bureau de Manhattan.
          Il a été construit, ligne par ligne, par une personne qui a d'abord passé <strong className="text-white">dix ans à galérer</strong> à
          apprendre le trading en marge du système traditionnel.
        </p>
      </section>

      {/* Story blocks */}
      <section className="max-w-4xl mx-auto px-6 space-y-16 py-10">
        <StoryBlock
          icon={Compass}
          tint="text-orange-300 bg-orange-500/10 border-orange-500/30"
          heading="2015 — Le premier clic"
          testid="about-story-1"
        >
          <p>
            Tout commence il y a une dizaine d'années. Le trading était présenté comme une porte de sortie, une possibilité de créer
            de la liberté financière sans réseau, sans capital de départ énorme, sans école de commerce. La curiosité s'est vite muée
            en <strong className="text-white">passion dévorante</strong>. Les graphiques, les cassures de structure, les zones où
            l'institutionnel intervient… tout ce vocabulaire mystérieux fascinait.
          </p>
          <p>
            Mais il y avait un problème : <strong className="text-white">pas de diplôme en finance</strong>, pas les moyens de payer une école
            spécialisée à 15 000 € l'année, pas de mentor derrière soi. Que faire ?
          </p>
        </StoryBlock>

        <StoryBlock
          icon={BookOpen}
          tint="text-yellow-300 bg-yellow-500/10 border-yellow-500/30"
          heading="Les années YouTube et l'auto-formation sauvage"
          testid="about-story-2"
        >
          <p>
            Comme beaucoup, le chemin a commencé par les <strong className="text-white">pseudo-formations YouTube</strong>. Des heures et des heures
            de vidéos à absorber. Certaines étaient géniales. La plupart étaient un mélange de survente, de contradictions
            et de <em className="text-white/70">"achetez ma formation à 2 000 € pour découvrir MON secret"</em>.
          </p>
          <p>
            Les <strong className="text-white">académies proposées par les prop firms</strong> ont apporté un peu plus de rigueur — au prix
            de challenges à répéter, de règles complexes à intégrer, et surtout d'outils propriétaires qui restaient <em>opaques</em> :
            des indicateurs "magiques" livrés en boîte noire, sans jamais comprendre ce qu'ils calculaient réellement.
          </p>
        </StoryBlock>

        <StoryBlock
          icon={Target}
          tint="text-red-300 bg-red-500/10 border-red-500/30"
          heading="La frustration qui déclenche tout"
          testid="about-story-3"
        >
          <p>
            Après plusieurs années à tester des dizaines d'indicateurs payants, un constat brutal s'est imposé :
          </p>
          <ul className="list-disc list-inside space-y-1 text-white/70 ml-2">
            <li>Aucun ne montrait <strong className="text-white">clairement</strong> pourquoi il donnait un signal.</li>
            <li>Chaque prop firm avait ses propres règles, aucune application ne les suivait <strong className="text-white">en temps réel</strong>.</li>
            <li>Les backtests étaient <strong className="text-white">invérifiables</strong> — impossible de savoir si on nous mentait.</li>
            <li>Il fallait <strong className="text-white">jongler entre 5 outils</strong> pour couvrir une même analyse (kill zones, PDH, order blocks…).</li>
          </ul>
          <p className="mt-3">
            C'est de cette frustration qu'est né ANATRADEX : <strong className="text-white">l'outil que j'aurais voulu avoir</strong> dès
            le premier jour, il y a dix ans.
          </p>
        </StoryBlock>

        <StoryBlock
          icon={Sparkles}
          tint="text-blue-300 bg-accent-blue/10 border-accent-blue/30"
          heading="La vision derrière ANATRADEX"
          testid="about-story-4"
        >
          <p>
            ANATRADEX repose sur trois principes qui découlent directement de ce parcours :
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <Pillar title="Transparence radicale" desc="Chaque calcul est explicable. Chaque backtest est vérifiable par audit hash-chain SHA-256." />
            <Pillar title="Tout-en-un lisible" desc="10 modules d'analyse dans un seul indicateur, activables à la carte, sans dépendance à une boîte noire." />
            <Pillar title="Prop-firm-friendly" desc="Suivi temps réel des règles FTMO, FundedNext, The5%ers… pour ne plus jamais perdre un compte sur une bêtise." />
          </div>
        </StoryBlock>

        <StoryBlock
          icon={ShieldCheck}
          tint="text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
          heading="Pour qui ? Pour vous."
          testid="about-story-5"
        >
          <p>
            ANATRADEX s'adresse aux <strong className="text-white">traders sérieux qui refusent la boîte noire</strong> :
            ceux qui veulent comprendre pourquoi leur outil dit ce qu'il dit, ceux qui ne veulent plus perdre un challenge FTMO
            à cause d'une règle mal suivie, ceux qui préfèrent apprendre plutôt que suivre aveuglément.
          </p>
          <p>
            Que vous soyez scalpeur, day-trader, swing-trader ou en train de passer votre premier challenge prop firm —
            ANATRADEX est votre <strong className="text-white">tour de contrôle</strong>.
          </p>
        </StoryBlock>
      </section>

      {/* Signature */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="border-l-2 border-accent-blue/40 pl-6 py-2 mt-8">
          <p className="text-white/70 italic leading-relaxed">
            « Je construis ANATRADEX avec la même exigence que j'aurais voulu qu'on ait envers moi quand j'ai débuté.
            Aucune boîte noire, aucune promesse tape-à-l'œil. Juste des outils qui vous respectent. »
          </p>
          <p className="mt-3 text-accent-blue font-heading font-semibold" data-testid="about-signature">
            — F.L., fondateur de ANATRADEX
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-24">
        <div className="panel p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-accent-blue/80 mb-2">
              <Users size={13} /> Rejoignez la Beta
            </div>
            <h3 className="font-heading text-2xl font-bold mb-1">Envie de tester avant tout le monde ?</h3>
            <p className="text-white/60 text-sm max-w-xl">
              La Beta est réservée aux traders qui ont un vrai use-case (prop firm, swing rigoureux, systématique).
              Racontez-nous le vôtre en 3 minutes.
            </p>
          </div>
          <Link to="/beta" className="btn btn-primary shrink-0" data-testid="about-cta-beta">
            Postuler à la Beta <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <span>© 2026 ANATRADEX — Tous droits réservés.</span>
          <div className="flex gap-4">
            <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
            <Link to="/risk-disclosure" className="hover:text-white">Risques</Link>
            <Link to="/terms" className="hover:text-white">CGU</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StoryBlock({ icon: Icon, tint, heading, children, testid }) {
  return (
    <div className="grid md:grid-cols-[80px_1fr] gap-6" data-testid={testid}>
      <div className={`w-14 h-14 rounded-xl border grid place-items-center ${tint}`}>
        <Icon size={22} />
      </div>
      <div>
        <h2 className="font-heading text-xl font-semibold mb-3 text-white">{heading}</h2>
        <div className="text-white/60 space-y-3 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Pillar({ title, desc }) {
  return (
    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
      <div className="font-heading font-semibold text-sm mb-1.5 text-white">{title}</div>
      <div className="text-[13px] text-white/55 leading-relaxed">{desc}</div>
    </div>
  );
}
